import { ReactChild } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Dropdown from 'dashboard/components/Navbar/Dropdown';
import NavbarLink from 'dashboard/components/Navbar/NavbarLink';
import { NavItem } from 'dashboard/models/role';

export interface NavbarItemData {
  id: string;
  label: ReactChild;
  labelText?: string;
  hidden?: boolean;
  prefixIcon: ReactChild;
  links: NavbarLinkData[];
  disabled?: boolean;
  navItem: NavItem;
  navItemRedesign: NavItem;
}

export interface NavbarLinkData {
  id: string;
  label: ReactChild;
  labelText?: string;
  navItem: NavItem;
  navItemRedesign: NavItem;
  hidden?: boolean;
  route?: string;
  disabled?: boolean;
}

export interface NavbarItemProps {
  isCollapsed: boolean;
  item: NavbarItemData;
  visibleLinks: NavbarLinkData[];
}

const NavbarItem = ({ isCollapsed, item, visibleLinks }: NavbarItemProps) => {
  const location = useLocation();

  if (visibleLinks.length === 1 || item.disabled) {
    const link = visibleLinks[0];

    return (
      <NavbarLink
        dataTest={`navbar__trigger--${item.id}`}
        selected={visibleLinks.some(
          (link) => link.route && location.pathname.startsWith(link.route),
        )}
        isCollapsed={isCollapsed}
        prefixIcon={item.prefixIcon}
        label={item.label}
        disabled={item.disabled || link.disabled}
        route={link.route}
      />
    );
  } else {
    return (
      <Dropdown
        items={visibleLinks.map((link) => (
          <NavbarLinkItemStretcher key={link.id}>
            <NavbarLink
              dataTest={`navbar__item--${link.id}`}
              selected={
                // Some routes that use shared prefixes need exact matches
                ['/dashboard/reports-analytics'].includes(location.pathname)
                  ? location.pathname === link.route
                  : !!(link.route && location.pathname.startsWith(link.route))
              }
              label={link.label}
              route={link.route}
              disabled={link.disabled}
            />
          </NavbarLinkItemStretcher>
        ))}
        triggerOnHover={true}
      >
        {({ isOpen, openDropdown, closeDropdown }) => (
          <div
            data-test='navbar__dropdown'
            onMouseOver={openDropdown}
            onMouseOut={closeDropdown}
            onFocus={openDropdown}
            onBlur={closeDropdown}
          >
            <NavbarLink
              dataTest={`navbar__trigger--${item.id}`}
              selected={
                isOpen ||
                item.links.some((link) => link.route && location.pathname.startsWith(link.route))
              }
              isCollapsed={isCollapsed}
              prefixIcon={item.prefixIcon}
              label={item.label}
            />
          </div>
        )}
      </Dropdown>
    );
  }
};

const NavbarLinkItemStretcher = styled.div`
  min-width: 200px;
`;

export default NavbarItem;
